/**
 * 记忆系统类型定义
 *
 * 定义用户记忆和原子记忆相关的类型。
 */

export type UserMemoryType = 'preference' | 'style' | 'intent' | 'habit' | 'special';

export type WorkspaceMemoryType = 'writing_preference' | 'writing_process' | 'language_style' | 'special';

export type MemoryType = UserMemoryType;

export interface UserMemory {
  id: number;
  user_id: number;
  memory_type: UserMemoryType;
  content: string;
  reinforcement_count: number;
  num_times: number;
  token_count: number;
  last_seen: string;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceMemory {
  id: number;
  user_id: number;
  workspace_id: number;
  memory_type: WorkspaceMemoryType;
  content: string;
  reinforcement_count: number;
  num_times: number;
  token_count: number;
  last_seen: string;
  created_at: string;
  updated_at: string;
}

export interface SkillDocument {
  id: number;
  user_id: number;
  name: string;
  description: string;
  steps: string[];
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface MemoryStats {
  user_memory_count: number;
  user_memory_tokens: number;
  atomic_memory_count: number;
}

export interface WorkspaceMemoryStats {
  workspace_memory_count: number;
  workspace_memory_tokens: number;
}

export interface UserMemoryTypeLabel {
  type: UserMemoryType;
  label: string;
  description: string;
  color: string;
}

export interface WorkspaceMemoryTypeLabel {
  type: WorkspaceMemoryType;
  label: string;
  description: string;
  color: string;
}

export const USER_MEMORY_TYPE_LABELS: UserMemoryTypeLabel[] = [
  {
    type: 'preference',
    label: '用户偏好',
    description: '你喜欢或不喜欢的风格、设定等',
    color: 'bg-blue-100 text-blue-800',
  },
  {
    type: 'style',
    label: '创作风格',
    description: '你的用词、句式、叙事视角等',
    color: 'bg-purple-100 text-purple-800',
  },
  {
    type: 'intent',
    label: '用户意图',
    description: '你想要达成的创作目标',
    color: 'bg-green-100 text-green-800',
  },
  {
    type: 'habit',
    label: '使用习惯',
    description: '你与AI交互的方式',
    color: 'bg-orange-100 text-orange-800',
  },
  {
    type: 'special',
    label: '特殊要求',
    description: '你特别要求记住的内容',
    color: 'bg-pink-100 text-pink-800',
  },
];

export const WORKSPACE_MEMORY_TYPE_LABELS: WorkspaceMemoryTypeLabel[] = [
  {
    type: 'writing_preference',
    label: '写作偏好',
    description: '本项目特定的写作设定',
    color: 'bg-blue-100 text-blue-800',
  },
  {
    type: 'writing_process',
    label: '写作流程',
    description: '本项目特定的写作顺序',
    color: 'bg-cyan-100 text-cyan-800',
  },
  {
    type: 'language_style',
    label: '语言风格',
    description: '本项目特定的语言表达方式',
    color: 'bg-purple-100 text-purple-800',
  },
  {
    type: 'special',
    label: '特殊要求',
    description: '本项目特定的特殊要求',
    color: 'bg-pink-100 text-pink-800',
  },
];

export function getUserMemoryTypeLabel(type: UserMemoryType): UserMemoryTypeLabel {
  return USER_MEMORY_TYPE_LABELS.find((l) => l.type === type) || USER_MEMORY_TYPE_LABELS[0];
}

export function getWorkspaceMemoryTypeLabel(type: WorkspaceMemoryType): WorkspaceMemoryTypeLabel {
  return WORKSPACE_MEMORY_TYPE_LABELS.find((l) => l.type === type) || WORKSPACE_MEMORY_TYPE_LABELS[0];
}

export interface UserMemoriesResponse {
  memories: UserMemory[];
  stats: MemoryStats;
}

export interface WorkspaceMemoriesResponse {
  memories: WorkspaceMemory[];
  stats: WorkspaceMemoryStats;
}

export interface CreateMemoryRequest {
  memory_type: UserMemoryType | WorkspaceMemoryType;
  content: string;
}

export interface UpdateMemoryRequest {
  content: string;
}

export interface SkillDocumentsResponse {
  skills: SkillDocument[];
}
